import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { toast } from "react-hot-toast";

import { useAuth } from "../context/AuthContext";

import {
  getBillingInfo,
  getInvoices,
  getLastInvoiceSummary,
  generateInvoice,
} from "../services/billingService";

import CurrentPlanCard from "../components/billing/CurrentPlanCard";
import BillingInfoCard from "../components/billing/BillingInfoCard";
import InvoiceTable from "../components/billing/InvoiceTable";
import EmptyInvoices from "../components/billing/EmptyInvoices";
import LastInvoiceSummaryCard from "../components/billing/LastInvoiceSummaryCard";

/* ====================================================== */
/* HELPERS                                                */
/* ====================================================== */

const isSameBillingMonth =
  (date) => {

    if (!date) {
      return false;
    }

    const invoiceDate =
      new Date(date);

    const now =
      new Date();

    return (
      invoiceDate.getMonth() ===
        now.getMonth() &&
      invoiceDate.getFullYear() ===
        now.getFullYear()
    );

  };

/* ====================================================== */
/* BILLING VALIDATION                                     */
/* ====================================================== */
/**
 * IMPORTANT:
 * GSTIN is OPTIONAL
 * PAN is OPTIONAL
 *
 * Invoice generation
 * should NOT fail
 * for missing GST.
 */

const isBillingComplete =
  (company) => {

    if (!company) {
      return false;
    }

    // Only need a company record

    return Boolean(
      company?.name ||
      company?.displayName ||
      company?.legalName
    );

  };

/* ====================================================== */
/* COMPONENT                                              */
/* ====================================================== */

export default function Billing() {

  const {
    activeCompany,
    refreshCompanies,
  } = useAuth();

  const companyId =
    activeCompany?._id;

  /* -------------------------------------------------- */
  /* STATE                                              */
  /* -------------------------------------------------- */

  const [
    billingInfo,
    setBillingInfo,
  ] = useState(null);

  const [
    invoices,
    setInvoices,
  ] = useState([]);

  const [
    lastInvoice,
    setLastInvoice,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    generating,
    setGenerating,
  ] = useState(false);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  /* ====================================================== */
  /* LOAD DATA                                              */
  /* ====================================================== */

  const loadBillingData =
    useCallback(
      async ({
        silent = false,
      } = {}) => {

        if (!companyId) {

          setBillingInfo(null);
          setInvoices([]);
          setLastInvoice(null);
          setLoading(false);

          return;

        }

        try {

          if (silent) {
            setRefreshing(true);
          } else {
            setLoading(true);
          }

          setError("");

          const [
            companyResult,
            invoicesResult,
            summaryResult,
          ] =
            await Promise.allSettled([
              getBillingInfo(
                companyId
              ),

              getInvoices(),

              getLastInvoiceSummary(),
            ]);

          /* ------------------------------------------ */
          /* COMPANY                                    */
          /* ------------------------------------------ */

          if (
            companyResult.status ===
            "fulfilled"
          ) {

            setBillingInfo(
              companyResult.value ||
                null
            );

          } else {

            console.error(
              "[Billing] Failed to load billing info:",
              companyResult.reason
            );

            setBillingInfo(
              null
            );

          }

          /* ------------------------------------------ */
          /* INVOICES                                   */
          /* ------------------------------------------ */

          if (
            invoicesResult.status ===
            "fulfilled"
          ) {

            setInvoices(
              Array.isArray(
                invoicesResult.value
              )
                ? invoicesResult.value
                : []
            );

          } else {

            console.error(
              "[Billing] Failed to load invoices:",
              invoicesResult.reason
            );

            setInvoices([]);

          }

          /* ------------------------------------------ */
          /* LAST INVOICE                               */
          /* ------------------------------------------ */

          if (
            summaryResult.status ===
            "fulfilled"
          ) {

            setLastInvoice(
              summaryResult.value ||
                null
            );

          } else {

            console.error(
              "[Billing] Failed to load invoice summary:",
              summaryResult.reason
            );

            setLastInvoice(
              null
            );

          }

        } catch (err) {

          console.error(
            "[Billing] Fatal load error:",
            err
          );

          setError(
            err?.message ||
              "Failed to load billing information"
          );

        } finally {

          setLoading(false);
          setRefreshing(false);

        }

      },
      [companyId]
    );

  /* ====================================================== */
  /* INITIAL LOAD                                            */
  /* ====================================================== */

  useEffect(() => {

    loadBillingData();

  }, [loadBillingData]);

  /* ====================================================== */
  /* DERIVED                                                */
  /* ====================================================== */

  const billingComplete =
    useMemo(
      () =>
        isBillingComplete(
          billingInfo
        ),
      [billingInfo]
    );

  const currentPlan =
    billingInfo?.plan ||
    "free";

  const isPaidPlan =
    currentPlan !==
    "free";

  const hasInvoiceThisMonth =
    useMemo(() => {

      if (
        !lastInvoice
          ?.invoiceDate
      ) {
        return false;
      }

      return isSameBillingMonth(
        lastInvoice.invoiceDate
      );

    }, [lastInvoice]);

  const canGenerateInvoice =
    useMemo(() => {

      if (
        !billingInfo
      ) {
        return false;
      }

      if (
        !isPaidPlan
      ) {
        return false;
      }

      if (
        !billingComplete
      ) {
        return false;
      }

      if (
        hasInvoiceThisMonth
      ) {
        return false;
      }

      return true;

    }, [
      billingInfo,
      isPaidPlan,
      billingComplete,
      hasInvoiceThisMonth,
    ]);

  /* ====================================================== */
  /* GENERATE INVOICE                                       */
  /* ====================================================== */

  const handleGenerateInvoice =
    async () => {

      try {

        if (
          generating
        ) {
          return;
        }

        if (
          !billingInfo
            ?.plan
        ) {

          toast.error(
            "Plan information missing"
          );

          return;

        }

        setGenerating(
          true
        );

        const result =
          await generateInvoice(
            billingInfo.plan
          );

        if (
          result?.skipped
        ) {

          toast.success(
            result?.message ||
              "Invoice already exists"
          );

        } else {

          toast.success(
            result?.message ||
              "Invoice generated successfully"
          );

        }

        await Promise.all([
          loadBillingData({
            silent: true,
          }),

          refreshCompanies?.(),
        ]);

      } catch (err) {

        console.error(
          "[Billing] Invoice generation failed:",
          err
        );

        toast.error(
          err?.message ||
            "Failed to generate invoice"
        );

      } finally {

        setGenerating(
          false
        );

      }

    };

  /* ====================================================== */
  /* LOADING                                                */
  /* ====================================================== */

  if (loading) {

    return (
      <div className="mx-auto max-w-6xl p-6">

        <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
          Loading billing information...
        </div>

      </div>
    );

  }

  /* ====================================================== */
  /* UI                                                     */
  /* ====================================================== */

  return (
    <div className="mx-auto max-w-6xl space-y-8 p-6">

      {/* ------------------------------------------------ */}
      {/* ERROR                                            */}
      {/* ------------------------------------------------ */}

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* ------------------------------------------------ */}
      {/* PLAN                                             */}
      {/* ------------------------------------------------ */}

      <CurrentPlanCard
        plan={currentPlan}
      />

      {/* ------------------------------------------------ */}
      {/* LAST INVOICE                                     */}
      {/* ------------------------------------------------ */}

      <LastInvoiceSummaryCard
        invoice={lastInvoice}
      />

      {/* ------------------------------------------------ */}
      {/* BILLING INFO                                     */}
      {/* ------------------------------------------------ */}

      <BillingInfoCard />

      {/* ------------------------------------------------ */}
      {/* BILLING WARNING                                  */}
      {/* ------------------------------------------------ */}

      {isPaidPlan &&
        !billingComplete && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">

            <div className="font-medium">
              Billing profile incomplete
            </div>

            <div className="mt-1">
              Please complete your billing information to enable invoice generation.
            </div>

          </div>
        )}

      {/* ------------------------------------------------ */}
      {/* INVOICES                                         */}
      {/* ------------------------------------------------ */}

      {isPaidPlan && (
        <section className="space-y-4">

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

            <div>

              <h2 className="text-lg font-semibold text-gray-900">
                Invoices
              </h2>

              <p className="text-sm text-gray-500">
                Download and manage billing invoices.
              </p>

            </div>

            <div className="flex items-center gap-3">

              {refreshing && (
                <span className="text-xs text-gray-500">
                  Refreshing...
                </span>
              )}

              {canGenerateInvoice && (
                <button
                  onClick={
                    handleGenerateInvoice
                  }
                  disabled={
                    generating
                  }
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {generating
                    ? "Generating..."
                    : "Generate Invoice"}
                </button>
              )}

            </div>

          </div>

          {hasInvoiceThisMonth && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
              Invoice already generated for this billing cycle.
            </div>
          )}

          {invoices.length ===
          0 ? (
            <EmptyInvoices />
          ) : (
            <InvoiceTable
              invoices={
                invoices
              }
            />
          )}

        </section>
      )}

    </div>
  );

}